import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import {
  Truck,
  ExternalLink,
  Coins,
  Clock,
  ArrowRight,
  CheckCircle2,
  ScanBarcode,
  Sliders,
} from 'lucide-react';
import {
  fetchShippingSummary,
  fetchShippingParcels,
  fetchShippingCarriers,
} from '../api.js';
import { PageHeader } from '../ui/PageHeader.js';
import { Button } from '../ui/Button.js';
import { Panel } from '../ui/panels.js';
import { Loading } from '../ui/feedback.js';
import { formatTnd } from '../lib/format.js';


export function LogisticsOverviewPage(): React.ReactElement {
  const summaryQuery = useQuery({
    queryKey: ['shipping', 'summary', 'all'],
    queryFn: () => fetchShippingSummary({ carrier: 'all' }),
  });

  const carriersQuery = useQuery({
    queryKey: ['shipping', 'carriers'],
    queryFn: fetchShippingCarriers,
  });

  // Recent parcels for the Tendering / Status Board (inspired by NexusFlow 4.webp)
  const recentInTransitQuery = useQuery({
    queryKey: ['shipping', 'overview-in-transit'],
    queryFn: () => fetchShippingParcels({ status: 'reconciled_in_transit', limit: 4 }),
  });

  const recentDeliveredQuery = useQuery({
    queryKey: ['shipping', 'overview-delivered'],
    queryFn: () => fetchShippingParcels({ status: 'reconciled_paid', limit: 4 }),
  });

  const recentExceptionsQuery = useQuery({
    queryKey: ['shipping', 'overview-exceptions'],
    queryFn: () => fetchShippingParcels({ status: 'price_mismatch', limit: 4 }),
  });

  const recentUnassignedQuery = useQuery({
    queryKey: ['shipping', 'overview-unassigned'],
    queryFn: () => fetchShippingParcels({ status: 'unassigned', limit: 4 }),
  });

  const summary = summaryQuery.data?.summary;
  const totalShiparon = summary?.totalShiparonParcels ?? 0;
  const matchedCount = summary?.matchedCount ?? 0;
  const paidCount = summary?.paidCount ?? 0;
  const ghostedCount = summary?.ghostedCount ?? 0;
  const underpaidCount = summary?.underpaidCount ?? 0;
  const unassignedCount = summary?.unassignedCount ?? 0;

  const otdRate = totalShiparon > 0 ? Math.round((matchedCount / totalShiparon) * 1000) / 10 : 0;

  return (
    <div className="space-y-6 animate-fade">
      {/* Top Page Header */}
      <PageHeader
        index="S13"
        eyebrow="Gestion Logistique · Pilotage"
        title="Tableau de Bord & Orchestration Logistique"
        purpose="Supervision en direct des expéditions multi-transporteurs, flux de livraison, tournées et contrôle des exceptions."
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Link to="/shipping/returns-scan">
              <Button variant="primary" size="sm" className="bg-emerald-700 hover:bg-emerald-800 text-white font-medium shadow-xs">
                <ScanBarcode className="h-4 w-4 mr-1.5" />
                Scan Réception Retours
              </Button>
            </Link>
            <Link to="/shipping/reconciliation">
              <Button variant="outline" size="sm">
                <Truck className="h-4 w-4 mr-1.5 text-ink-soft" />
                Réconciliation Colis
              </Button>
            </Link>
            <Link to="/shipping/config">
              <Button variant="outline" size="sm">
                <Sliders className="h-4 w-4 mr-1.5 text-ink-soft" />
                Configuration
              </Button>
            </Link>
            <Link to="/suivi" target="_blank">
              <Button variant="outline" size="sm">
                <ExternalLink className="h-4 w-4 mr-1.5 text-ink-soft" />
                Suivi Client
              </Button>
            </Link>
          </div>
        }
      />


      {summaryQuery.isLoading ? (
        <div className="py-16 text-center">
          <Loading label="Chargement de la vue d'ensemble logistique…" />
        </div>
      ) : (
        <>
          {/* Top Metrics Strip (inspired by NexusFlow 4.webp & CargoDesk 5.webp) */}
          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-5 gap-3.5">
            {/* 1. In Transit */}
            <Panel className="p-4 border-border bg-card shadow-xs space-y-1.5">
              <div className="flex items-center justify-between text-xs text-ink-soft">
                <span className="font-semibold uppercase tracking-wider text-[11px]">En Transit</span>
                <span className="h-2 w-2 rounded-full bg-sky-500 animate-pulse" />
              </div>
              <div className="text-2xl font-bold font-mono text-ink">
                {(totalShiparon - matchedCount - unassignedCount).toLocaleString('fr-FR')}
              </div>
              <p className="text-[11px] text-ink-soft">Réseau Jetpack & Fiabilo</p>
            </Panel>

            {/* 2. OTD Rate */}
            <Panel className="p-4 border-border bg-card shadow-xs space-y-1.5">
              <div className="flex items-center justify-between text-xs text-ink-soft">
                <span className="font-semibold uppercase tracking-wider text-[11px]">Taux Réussite</span>
                <span className="text-emerald-700 text-[10px] font-bold bg-emerald-50 px-1.5 py-0.2 rounded-full border border-emerald-200">
                  OTD
                </span>
              </div>
              <div className="text-2xl font-bold font-mono text-emerald-800">
                {otdRate}%
              </div>
              <p className="text-[11px] text-ink-soft">{matchedCount.toLocaleString('fr-FR')} colis livrés</p>
            </Panel>

            {/* 3. Exceptions & Litiges */}
            <Panel className="p-4 border-border bg-card shadow-xs space-y-1.5">
              <div className="flex items-center justify-between text-xs text-ink-soft">
                <span className="font-semibold uppercase tracking-wider text-[11px]">Exceptions</span>
                {ghostedCount > 0 ? (
                  <span className="text-rose-700 text-[10px] font-bold bg-rose-50 px-1.5 py-0.2 rounded-full border border-rose-200">
                    Alerte
                  </span>
                ) : null}
              </div>
              <div className="text-2xl font-bold font-mono text-rose-700">
                {(ghostedCount + underpaidCount).toLocaleString('fr-FR')}
              </div>
              <p className="text-[11px] text-ink-soft">
                {ghostedCount} sans trace · {underpaidCount} sous-payés
              </p>
            </Panel>

            {/* 4. Payés & Rapprochés */}
            <Panel className="p-4 border-border bg-card shadow-xs space-y-1.5">
              <div className="flex items-center justify-between text-xs text-ink-soft">
                <span className="font-semibold uppercase tracking-wider text-[11px]">Payés / Déclarés</span>
                <Coins className="h-3.5 w-3.5 text-emerald-600" />
              </div>
              <div className="text-2xl font-bold font-mono text-ink">
                {paidCount.toLocaleString('fr-FR')}
              </div>
              <p className="text-[11px] text-ink-soft">
                {formatTnd(summary?.paidAmount)}
              </p>
            </Panel>

            {/* 5. Non assignés */}
            <Panel className="p-4 border-border bg-card shadow-xs space-y-1.5 col-span-2 sm:col-span-1">
              <div className="flex items-center justify-between text-xs text-ink-soft">
                <span className="font-semibold uppercase tracking-wider text-[11px]">Non assignés</span>
                <Clock className="h-3.5 w-3.5 text-amber-600" />
              </div>
              <div className="text-2xl font-bold font-mono text-ink">
                {unassignedCount.toLocaleString('fr-FR')}
              </div>
              <p className="text-[11px] text-ink-soft">À affecter à un coursier</p>
            </Panel>
          </div>

          {/* Carrier Distribution Hub (inspired by CargoDesk 5.webp Hub Overview) */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {carriersQuery.data?.carriers.map((c) => (
              <Panel key={c.carrier} className="p-4 border-border bg-card shadow-xs flex items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-xl bg-slate-100 flex items-center justify-center text-slate-700 font-bold text-sm">
                    {c.carrier.slice(0, 2).toUpperCase()}
                  </div>
                  <div>
                    <h4 className="text-sm font-bold text-ink">{c.carrier}</h4>
                    <p className="text-xs text-ink-soft font-mono">
                      {c.count.toLocaleString('fr-FR')} colis confiés
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <span className="text-xs font-mono font-bold text-ink block">
                    {formatTnd(c.totalAmount)}
                  </span>
                  <Link
                    to={`/shipping/reconciliation`}
                    className="text-[11px] font-semibold text-emerald-700 hover:underline inline-flex items-center gap-0.5 mt-0.5"
                  >
                    Voir colis <ArrowRight className="h-3 w-3" />
                  </Link>
                </div>
              </Panel>
            ))}
          </div>

          {/* Tendering & Status Board / Kanban (inspired by NexusFlow 4.webp) */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-base font-bold text-ink">Flux Opérationnel des Expéditions (Tendering Board)</h3>
                <p className="text-xs text-ink-soft">
                  Aperçu temps réel des flux colis selon leur étape de traitement logistique.
                </p>
              </div>
              <Link
                to="/shipping/reconciliation"
                className="text-xs font-semibold text-emerald-800 hover:underline flex items-center gap-1"
              >
                Ouvrir la table complète ({totalShiparon.toLocaleString('fr-FR')} colis) <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Column 1: Non assignés */}
              <div className="bg-slate-100/70 p-3 rounded-2xl border border-slate-200/80 space-y-3">
                <div className="flex items-center justify-between px-1">
                  <div className="flex items-center gap-2">
                    <span className="h-2 w-2 rounded-full bg-slate-400" />
                    <span className="text-xs font-bold text-slate-800">Non assignés</span>
                  </div>
                  <span className="text-xs font-mono font-bold bg-white text-slate-700 px-2 py-0.5 rounded-full border border-slate-200">
                    {unassignedCount.toLocaleString('fr-FR')}
                  </span>
                </div>

                <div className="space-y-2">
                  {recentUnassignedQuery.data?.parcels.map((p) => (
                    <div key={p.codeBarre} className="bg-white p-3 rounded-xl border border-slate-200 shadow-2xs space-y-1.5">
                      <div className="flex items-center justify-between text-xs">
                        <span className="font-mono font-bold text-slate-900">{p.codeBarre}</span>
                        <span className="font-mono text-emerald-800 font-semibold">{formatTnd(p.shiparonPrice)}</span>
                      </div>
                      <p className="text-xs text-slate-600 truncate">{p.clientName || 'Destinataire non renseigné'}</p>
                      <div className="flex items-center justify-between text-[11px] text-slate-400 pt-1 border-t border-slate-100">
                        <span>{p.location || 'Tunisie'}</span>
                        <span>{p.date || '—'}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Column 2: En cours transporteur */}
              <div className="bg-sky-50/60 p-3 rounded-2xl border border-sky-100 space-y-3">
                <div className="flex items-center justify-between px-1">
                  <div className="flex items-center gap-2">
                    <span className="h-2 w-2 rounded-full bg-sky-500 animate-pulse" />
                    <span className="text-xs font-bold text-sky-950">En acheminement</span>
                  </div>
                  <span className="text-xs font-mono font-bold bg-white text-sky-800 px-2 py-0.5 rounded-full border border-sky-200">
                    Jetpack & co
                  </span>
                </div>

                <div className="space-y-2">
                  {recentInTransitQuery.data?.parcels.map((p) => (
                    <div key={p.codeBarre} className="bg-white p-3 rounded-xl border border-sky-200/80 shadow-2xs space-y-1.5">
                      <div className="flex items-center justify-between text-xs">
                        <span className="font-mono font-bold text-slate-900">{p.codeBarre}</span>
                        <span className="font-mono text-emerald-800 font-semibold">{formatTnd(p.shiparonPrice)}</span>
                      </div>
                      <div className="flex items-center justify-between text-xs text-slate-600">
                        <span className="truncate">{p.clientName || 'Client'}</span>
                        <span className="text-[10px] bg-sky-50 text-sky-700 px-1.5 py-0.2 rounded font-semibold shrink-0">
                          {p.carrier || 'Coursier'}
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-[11px] text-slate-400 pt-1 border-t border-slate-100">
                        <span>{p.location || 'Tunisie'}</span>
                        <span className="text-sky-700 font-medium">En tournée</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Column 3: Exceptions & Écarts */}
              <div className="bg-rose-50/60 p-3 rounded-2xl border border-rose-100 space-y-3">
                <div className="flex items-center justify-between px-1">
                  <div className="flex items-center gap-2">
                    <span className="h-2 w-2 rounded-full bg-rose-500" />
                    <span className="text-xs font-bold text-rose-950">Exceptions & Litiges</span>
                  </div>
                  <span className="text-xs font-mono font-bold bg-white text-rose-800 px-2 py-0.5 rounded-full border border-rose-200">
                    {(ghostedCount + underpaidCount).toLocaleString('fr-FR')}
                  </span>
                </div>

                <div className="space-y-2">
                  {recentExceptionsQuery.data?.parcels.map((p) => (
                    <div key={p.codeBarre} className="bg-white p-3 rounded-xl border border-rose-200/80 shadow-2xs space-y-1.5">
                      <div className="flex items-center justify-between text-xs">
                        <span className="font-mono font-bold text-slate-900">{p.codeBarre}</span>
                        <span className="font-mono text-rose-700 font-bold">
                          {p.priceDiff ? `${formatTnd(p.priceDiff)}` : 'Écart'}
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-xs text-slate-600">
                        <span className="truncate">{p.clientName || 'Client'}</span>
                        <span className="text-[10px] bg-rose-50 text-rose-700 px-1.5 py-0.2 rounded font-semibold shrink-0">
                          {p.mismatchType === 'underpaid' ? 'Sous-payé' : 'Écart COD'}
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-[11px] text-slate-400 pt-1 border-t border-slate-100">
                        <span>Attendu : {formatTnd(p.shiparonPrice)}</span>
                        <span className="text-rose-700 font-medium">À réclamer</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Column 4: Livrés & Validés */}
              <div className="bg-emerald-50/60 p-3 rounded-2xl border border-emerald-100 space-y-3">
                <div className="flex items-center justify-between px-1">
                  <div className="flex items-center gap-2">
                    <span className="h-2 w-2 rounded-full bg-emerald-500" />
                    <span className="text-xs font-bold text-emerald-950">Livrés & Payés</span>
                  </div>
                  <span className="text-xs font-mono font-bold bg-white text-emerald-800 px-2 py-0.5 rounded-full border border-emerald-200">
                    {paidCount.toLocaleString('fr-FR')}
                  </span>
                </div>

                <div className="space-y-2">
                  {recentDeliveredQuery.data?.parcels.map((p) => (
                    <div key={p.codeBarre} className="bg-white p-3 rounded-xl border border-emerald-200/80 shadow-2xs space-y-1.5">
                      <div className="flex items-center justify-between text-xs">
                        <span className="font-mono font-bold text-slate-900">{p.codeBarre}</span>
                        <span className="font-mono text-emerald-700 font-bold">{formatTnd(p.shiparonPrice)}</span>
                      </div>
                      <div className="flex items-center justify-between text-xs text-slate-600">
                        <span className="truncate">{p.clientName || 'Client'}</span>
                        <span className="text-[10px] bg-emerald-50 text-emerald-700 px-1.5 py-0.2 rounded font-semibold shrink-0">
                          {p.carrier || 'Livré'}
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-[11px] text-slate-400 pt-1 border-t border-slate-100">
                        <span>{p.location || 'Tunisie'}</span>
                        <span className="text-emerald-700 font-medium flex items-center gap-0.5">
                          <CheckCircle2 className="h-3 w-3" /> Encaissé
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
